/**
 * Web implementation of the FilePlatform context: downloads go through the authenticated file URL as an attachment
 * (`?download=1`, Content-Disposition: attachment), links are copied with the Clipboard API and confirmed by a toast
 * (shell/FilesToast.tsx listens to `onCopied`).
 */
import type { FileRef } from "@bundle/contracts";
import type { FilePlatform } from "@bundle/ui";
import { WEB_ORIGIN } from "../env.ts";
import type { MediaUrls } from "./media.ts";

export type WebFilePlatform = FilePlatform & {
  /** "Link copied" confirmations (the toast). */
  onCopied(listener: () => void): () => void;
};

function clickDownload(url: string, name: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.append(a);
  a.click();
  a.remove();
}

function copyWithSelection(text: string): boolean {
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.append(area);
  area.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    area.remove();
  }
}

export function createFilePlatform(media: MediaUrls): WebFilePlatform {
  const listeners = new Set<() => void>();
  return {
    download(file: FileRef) {
      const url = media.downloadUrl(file);
      if (url !== undefined) return clickDownload(url, file.name);
      const ws = /^\/v1\/w\/([^/]+)\//.exec(file.url)?.[1];
      if (ws === undefined) return;
      void media.ready(decodeURIComponent(ws)).then(() => {
        const later = media.downloadUrl(file);
        if (later !== undefined) clickDownload(later, file.name);
      });
    },
    copyLink(path: string) {
      // The web app's URL: on desktop this page is tauri://localhost, which no one else can open.
      const url = new URL(path, WEB_ORIGIN).toString();
      const done = () => {
        for (const l of Array.from(listeners)) l();
      };
      // The Clipboard API needs a focused document; older webviews only have execCommand.
      void navigator.clipboard.writeText(url).then(done, () => {
        if (copyWithSelection(url)) done();
      });
    },
    onCopied(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
